import type { CustomerPortalPermissionFlags } from "@/features/customerPortal/customerPortal.interfaces";
import { RoleEnum } from "@/interfaces/globals";
import { useAppSelector } from "@/redux/store";

/**
 * Resolves customer portal capability flags from the active customer session.
 *
 * @returns {CustomerPortalPermissionFlags} Customer portal permission flags.
 */
export const useCustomerPortalPermission =
  (): CustomerPortalPermissionFlags => {
    const customerData = useAppSelector((state) => state.user.customerData);
    const isCustomer = customerData?.customerUserRole === RoleEnum.CUSTOMER;

    if (isCustomer) {
      return {
        canAccessCustomerPortal: true,
        canAccessProjectFinance: true,
        canAccessEmailNotification: true,
        canAccessWorkDocket: true,
      };
    }

    return {
      canAccessCustomerPortal:
        customerData?.permissions?.customerPortal ?? false,
      canAccessProjectFinance:
        customerData?.permissions?.projectFinance ?? false,
      canAccessEmailNotification:
        customerData?.permissions?.emailNotification ?? false,
      canAccessWorkDocket: customerData?.permissions?.workDocket ?? false,
    };
  };

/**
 * Resolves whether the active customer user can make write changes.
 *
 * @returns {boolean} True when the customer role can edit portal content.
 */
export const useCustomerWritePermission = (): boolean => {
  const customerData = useAppSelector((state) => state.user.customerData);

  return Boolean(
    customerData?.customerUserRole === RoleEnum.ADMIN ||
    customerData?.customerUserRole === RoleEnum.CUSTOMER,
  );
};
